import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../../../api/generated';
import KendoLoading from '../../../../../components/spinners/KendoLoading';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import { INewTicketTemplate, RowStyles, toggleButtonStyles, validationSchema } from './GeneralSettingsTicketsTemplates';

interface IUpdateTicketTemplate extends INewTicketTemplate {
    id: number;
}
interface TicketTemplateEditWindowProps {
    ticketTemplateId: number;
    refetchTicketTemplates(): Promise<void>;
}
const TicketTemplateEditWindow: FC<IWindowComponentProps<TicketTemplateEditWindowProps>> = (props) => {
    const {
        componentProps: { ticketTemplateId, refetchTicketTemplates },
        onClose
    } = props;
    const [ticketTemplate, setTicketTemplate] = useState<IUpdateTicketTemplate | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                var resp = await api.ticketTemplate.getTicketTemplate(ticketTemplateId);
                setTicketTemplate(resp);
            } catch (ex) {
                toast.error('Nepavyko gauti duomenų');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleSubmit = async (values: IUpdateTicketTemplate, actions: formik.FormikHelpers<IUpdateTicketTemplate>) => {
        actions.setStatus();
        try {
            setIsLoading(true);
            await api.ticketTemplate.updateTicketTemplate(values);
            toast.success('Pakeitimai išsaugoti');
            refetchTicketTemplates();
            onClose();
        } catch (ex) {
            actions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {ticketTemplate && (
                        <formik.Formik
                            initialValues={{ ...ticketTemplate }}
                            onSubmit={handleSubmit}
                            validationSchema={validationSchema}
                            render={(formikProps) => (
                                <formik.Form>
                                    <div className="ms-Grid-row" style={RowStyles}>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <formik.Field
                                                name="templateName"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        autoFocus
                                                        label="Šablono pavadinimas"
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.templateName) ||
                                                            (form.errors.templateName && form.errors.templateName)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="isLogoEnabled"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Toggle
                                                        styles={toggleButtonStyles}
                                                        {...field}
                                                        label="Įstaigos logotipas"
                                                        defaultChecked={field.value}
                                                        onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        onText="Rodomas"
                                                        offText="Nerodomas"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="isOrganizationNameEnabled"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Toggle
                                                        {...field}
                                                        label="Įstaigos pavadinimas"
                                                        styles={toggleButtonStyles}
                                                        defaultChecked={field.value}
                                                        onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        onText="Rodomas"
                                                        offText="Nerodomas"
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>

                                    <div className="ms-Grid-row" style={RowStyles}>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="mainText"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Pagrindinis tekstas"
                                                            {...field}
                                                            errorMessage={
                                                                (formikProps.status && formikProps.status.mainText) ||
                                                                (form.errors.mainText && form.errors.mainText)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="isMainTextEnabled"
                                                    render={({ field, form }: formik.FieldProps<boolean>) => (
                                                        <fabric.Toggle
                                                            styles={toggleButtonStyles}
                                                            {...field}
                                                            defaultChecked={field.value}
                                                            label="Būsena"
                                                            onText="Rodomas"
                                                            offText="Nerodomas"
                                                            onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="isBarcodeEnabled"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Toggle
                                                        styles={toggleButtonStyles}
                                                        {...field}
                                                        defaultChecked={field.value}
                                                        label="Brūkšninis kodas"
                                                        onText="Rodomas"
                                                        offText="Nerodomas"
                                                        onChange={() => form.setFieldValue(field.name, !field.value)}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                                            <formik.Field
                                                name="isDateEnabled"
                                                render={({ field, form }: formik.FieldProps<boolean>) => (
                                                    <fabric.Toggle
                                                        {...field}
                                                        defaultChecked={field.value}
                                                        label="Data"
                                                        onText="Rodoma"
                                                        offText="Nerodoma"
                                                        styles={toggleButtonStyles}
                                                        onChange={() => form.setFieldValue(field.name, !field.value)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row" style={RowStyles}>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="cabinetText"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Kabineto tekstas "
                                                            {...field}
                                                            errorMessage={
                                                                (formikProps.status && formikProps.status.cabinetText) ||
                                                                (form.errors.cabinetText && form.errors.cabinetText)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="isCabinetTextEnabled"
                                                    render={({ field, form }: formik.FieldProps<boolean>) => (
                                                        <fabric.Toggle
                                                            {...field}
                                                            defaultChecked={field.value}
                                                            label="Būsena"
                                                            onText="Rodomas"
                                                            offText="Nerodomas"
                                                            styles={toggleButtonStyles}
                                                            onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row" style={RowStyles}>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="floorText"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Aukšto tekstas "
                                                            {...field}
                                                            errorMessage={
                                                                (formikProps.status && formikProps.status.floorText) ||
                                                                (form.errors.floorText && form.errors.floorText)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="isFloorTextEnabled"
                                                    render={({ field, form }: formik.FieldProps<boolean>) => (
                                                        <fabric.Toggle
                                                            {...field}
                                                            styles={toggleButtonStyles}
                                                            defaultChecked={field.value}
                                                            label="Būsena"
                                                            onText="Rodomas"
                                                            offText="Nerodomas"
                                                            onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row" style={RowStyles}>
                                        <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="timeText"
                                                    render={({ field, form }: formik.FieldProps<string>) => (
                                                        <fabric.TextField
                                                            label="Laiko tekstas"
                                                            {...field}
                                                            errorMessage={
                                                                (formikProps.status && formikProps.status.timeText) ||
                                                                (form.errors.timeText && form.errors.timeText)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="ms-Grid-col">
                                                <formik.Field
                                                    name="isTimeTextEnabled"
                                                    render={({ field, form }: formik.FieldProps<boolean>) => (
                                                        <fabric.Toggle
                                                            {...field}
                                                            styles={toggleButtonStyles}
                                                            defaultChecked={field.value}
                                                            label="Būsena"
                                                            onText="Rodomas"
                                                            offText="Nerodomas"
                                                            onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                            <fabric.PrimaryButton
                                                className="ms-slideRightIn40 ms-textAlignRight"
                                                text="Išsaugoti"
                                                disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                                type="submit"
                                            />
                                        </div>
                                    </div>
                                </formik.Form>
                            )}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default TicketTemplateEditWindow;
